import { Injectable } from '@angular/core';
import { DesktopService } from './desktop.service';
import EncodeUtils from '../utility/encode-utils';

class SendInfo {
  bytesSent?: number;
  resultCode: number;
}

class ReceiveInfo {
  data: ArrayBuffer;
  socketId: number;
}

class ReceiveErrorInfo {
  socketId: number;
  resultCode: number;
}

const delimiter = 0x00;

interface CordovaSocketSession {
  socketId: number;
  /** Ends the request exactly once (success or error). */
  settle: (error?: string, data?: ArrayBuffer) => void;
}

@Injectable()
export class SocketService {
  private _socket: any;
  private get socket(): any {
    if (!this._socket) {
      this._socket = (window as any).chrome?.sockets?.tcp;
    }
    return this._socket;
  }

  private _logsDisabled = true;

  /** Active Cordova send/receive sessions keyed by socketId. */
  private _sessions = new Map<number, CordovaSocketSession>();

  constructor(private desktop: DesktopService) {}

  async sendData(
    ipAddress: string,
    port: number,
    command: ArrayBuffer
  ): Promise<any> {
    if (!command) {
      return null;
    }

    // Desktop printers use DesktopService.netPrinterPrint; bank terminals use sendDataWithResponse.
    if (this.desktop.isDesktop) {
      return null;
    }

    if (!this.socket) {
      return null;
    }

    const socketId = await this._createSocket();

    const connection = await this._connect(socketId, ipAddress, port);
    if (connection.result < 0) {
      await this._closeSocket(socketId);
      return null;
    }

    return new Promise((resolve) => {
      this.socket.send(socketId, command, (sendInfo: SendInfo) => {
        this._closeSocket(socketId).then(() => {
          this._log('SOCKET CLOSED');
          resolve(sendInfo.resultCode);
        });
      });
    });
  }

  sendDataWithResponse(
    ipAddress: string,
    port: number,
    data: ArrayBuffer,
    timeout = 60000
  ): Promise<ArrayBuffer> {
    if (!data) {
      return Promise.reject('data_not_provided');
    }

    if (this.desktop.isDesktop) {
      return this._sendDataWithResponseDesktop(ipAddress, port, data, timeout);
    }

    return this._sendDataWithResponseCordova(ipAddress, port, data, timeout);
  }

  private _sendDataWithResponseCordova(
    ipAddress: string,
    port: number,
    data: ArrayBuffer,
    timeout: number
  ): Promise<ArrayBuffer> {
    if (!this.socket) {
      return Promise.reject('socket_unavailable');
    }

    this._log('____sendDataWithResponse (cordova)_____');

    return new Promise((resolve, reject) => {
      this.socket.create({}, (createInfo: any) => {
        const socketId = createInfo?.socketId;
        if (!(socketId > 0)) {
          reject('Socket not created');
          return;
        }

        let settled = false;
        const chunks: number[] = [];
        let timeoutId: any;

        const onReceive = (info: ReceiveInfo) => {
          if (settled || info.socketId !== socketId || !info.data) {
            return;
          }

          const packet = new Uint8Array(info.data);
          this._log('RECEIVE DATA byteLength=', packet.byteLength);

          for (let i = 0; i < packet.length; i++) {
            chunks.push(packet[i]);
          }

          // Message complete when accumulated buffer ends with 0x00 delimiter
          if (chunks.length > 0 && chunks[chunks.length - 1] === delimiter) {
            settle(undefined, new Uint8Array(chunks).buffer);
          }
        };

        const onReceiveError = (info: ReceiveErrorInfo) => {
          if (settled || info.socketId !== socketId) {
            return;
          }
          this._log('RECEIVE ERROR', info.resultCode);
          settle('receive_error');
        };

        const cleanup = () => {
          if (timeoutId != null) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          try {
            this.socket.onReceive.removeListener(onReceive);
          } catch {
            // ignore
          }
          try {
            this.socket.onReceiveError.removeListener(onReceiveError);
          } catch {
            // ignore
          }
          this._sessions.delete(socketId);
          this._closeSocket(socketId);
        };

        const settle = (error?: string, response?: ArrayBuffer) => {
          if (settled) {
            return;
          }
          settled = true;
          cleanup();
          if (error) {
            reject(error);
          } else {
            resolve(response);
          }
        };

        this.socket.onReceive.addListener(onReceive);
        this.socket.onReceiveError.addListener(onReceiveError);

        this._sessions.set(socketId, {
          socketId,
          settle: (error?: string, response?: ArrayBuffer) =>
            settle(error, response),
        });

        timeoutId = setTimeout(() => {
          settle('connection_timeout');
        }, timeout > 0 ? timeout : 60000);

        this.socket.connect(socketId, ipAddress, port, (result: number) => {
          this._log('_connect. result ', result);
          if (settled) {
            return;
          }
          if (result < 0) {
            settle('connection_error');
            return;
          }

          this.socket.send(socketId, data, (sendInfo: SendInfo) => {
            this._log('send. result', sendInfo);
            if (settled) {
              return;
            }
            if (!sendInfo || sendInfo.resultCode < 0) {
              settle('send_data_error');
            }
          });
        });
      });
    });
  }

  private async _sendDataWithResponseDesktop(
    ipAddress: string,
    port: number,
    data: ArrayBuffer,
    timeout: number
  ): Promise<ArrayBuffer> {
    const commandBase64 = await EncodeUtils.arraybufferToBase64Async(data);
    try {
      const responseBase64 = await this.desktop
        .socketSend(ipAddress, port, commandBase64, timeout)
        .toPromise();

      if (!responseBase64) {
        return Promise.reject('connection_error');
      }

      return EncodeUtils.base64ToArrayBuffer(responseBase64).buffer;
    } catch (err) {
      // Desktop host returns string errors: connection_timeout, operation_canceled, …
      return Promise.reject(err || 'connection_error');
    }
  }

  async checkConnection(ip: string, port: number): Promise<boolean> {
    if (this.desktop.isDesktop) {
      try {
        return await this.desktop.checkIpAddressAndPort(ip, port).toPromise();
      } catch {
        return false;
      }
    }

    if (!this.socket) {
      return false;
    }

    let socketId: number;
    try {
      socketId = await this._createSocket();
    } catch {
      return false;
    }

    try {
      const connection = await this._connect(socketId, ip, port, 800);
      return connection.result >= 0;
    } catch {
      return false;
    } finally {
      await this._closeSocket(socketId);
    }
  }

  private _connect(
    socketId: number,
    ipAddress: string,
    port: number,
    timeout: number = 0
  ): Promise<{ result: number; ipAddress: string; port: number }> {
    return new Promise((resolve, reject) => {
      let to: any;
      if (timeout > 0) {
        to = setTimeout(() => reject('Operation timed out.'), timeout);
      }

      this.socket.connect(socketId, ipAddress, port, (result: number) => {
        if (to) {
          clearTimeout(to);
        }

        resolve({ result, ipAddress, port });
      });
    });
  }

  private _createSocket(): Promise<number> {
    return new Promise((resolve, reject) => {
      this.socket.create({}, (createInfo: any) => {
        const socketTcpId = createInfo?.socketId;
        if (socketTcpId > 0) {
          resolve(socketTcpId);
        } else {
          reject('Socket not created');
        }
      });
    });
  }

  private _closeSocket(socketId: number): Promise<void> {
    return new Promise((resolve) => {
      try {
        this.socket.close(socketId, () => resolve());
      } catch {
        resolve();
      }
    });
  }

  /**
   * Cancel in-flight Cordova / desktop socket operations (cashier abort).
   */
  async disconnectAll(): Promise<any> {
    if (this.desktop.isDesktop) {
      this.desktop.socketDisconnect();
      return;
    }

    if (!this.socket) {
      return;
    }

    // Fail pending send/receive with the same code as desktop cancel
    const sessions = Array.from(this._sessions.values());
    for (const session of sessions) {
      session.settle('operation_canceled');
    }

    const socketsInfo = await this._getSockets().catch(() => [] as any[]);
    await Promise.all(
      (socketsInfo || [])
        .filter((s) => s && s.connected)
        .map((s) => this._closeSocket(s.socketId))
    );
  }

  private _getSockets(): Promise<any[]> {
    return new Promise((resolve) => {
      this.socket.getSockets((info: any[]) => {
        resolve(info || []);
      });
    });
  }

  private _log(msg: string, ...optionalParams: any[]) {
    if (this._logsDisabled) {
      return;
    }

    console.log(msg, ...optionalParams);
  }
}
